import { useEffect, useRef } from "react";
import { makeHive } from "./hiveLayer";

/* RIPP AI CORE — canvas, requestAnimationFrame, driven entirely by app state (no React re-render per frame).
   Look: dark segmented mechanical ring · violet→cyan energy annulus · dark inner disc with the glowing R ·
   thick liquid-energy tendrils to the agents. mode: idle | scanning | analyzing | confluence | risk | executing |
   position | target | scalein | risklock | offline */
const MODE = {
  idle: { speed: 0.25, hue: "56,189,248", glow: 0.45, breath: 0.03 },
  scanning: { speed: 0.6, hue: "56,189,248", glow: 0.6, breath: 0.04 },
  analyzing: { speed: 0.9, hue: "99,179,255", glow: 0.75, breath: 0.06 },
  confluence: { speed: 1.0, hue: "125,211,252", glow: 1.0, breath: 0.07 },
  risk: { speed: 0.7, hue: "245,185,66", glow: 0.85, breath: 0.09 },
  executing: { speed: 1.6, hue: "255,110,60", glow: 1.0, breath: 0.12 },
  position: { speed: 0.5, hue: "0,229,181", glow: 0.8, breath: 0.04 },
  target: { speed: 0.8, hue: "0,229,181", glow: 1.0, breath: 0.12 },
  scalein: { speed: 0.9, hue: "167,139,250", glow: 0.9, breath: 0.07 },
  risklock: { speed: 0.2, hue: "255,51,85", glow: 0.9, breath: 0.02 },
  offline: { speed: 0.0, hue: "113,113,122", glow: 0.2, breath: 0.0 },
};
const CORE_IMG = typeof Image !== "undefined" ? Object.assign(new Image(), { src: "/ripp-core-ring.png" }) : null;
const CYAN = "56,189,248", VIOLET = "167,139,250", MAGENTA = "217,70,239", WHITE = "236,244,255";
const toneCol = (a, i) => (a.tone === "bad" ? MAGENTA : a.tone === "warn" ? "245,185,66" : a.tone === "good" ? CYAN : i % 2 ? VIOLET : CYAN);

// Shared draw loop — the dashboard canvas and the 3D showcase (as a live texture) run the exact same Core.
export function runCore(cv, { size, compact = false, live, dprMax = 2 }) {
  const withHive = !compact && size >= 160, off = withHive ? Math.round(size * 0.45) : 0, full = size + off * 2;
    const ctx = cv.getContext("2d");
    const dpr = Math.min(dprMax, window.devicePixelRatio || 1);
    cv.width = full * dpr; cv.height = full * dpr;
    ctx.scale(dpr, dpr); ctx.translate(off, off);
    // OS "reduce motion" softens the Core (slower, no travelling pulses) but never freezes it.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const motion = reduced ? 0.35 : 1;
    let raf, t = 0, rot = 0, cur = { glow: 0.4, speed: 0.3 };
    const R = size / 2;
    const hasConic = typeof ctx.createConicGradient === "function";
    const hive = withHive ? makeHive(R, size, reduced, off) : null;

    const tendril = (ang, len, col, active, i) => {
      // liquid-energy tendril from the ring edge out to the agent node: wide glow → colour → white core
      const r0 = R * 0.5, r1 = R * len;
      const x0 = R + Math.cos(ang) * r0, y0 = R + Math.sin(ang) * r0, x1 = R + Math.cos(ang) * r1, y1 = R + Math.sin(ang) * r1;
      const w = Math.sin(t * 1.3 + i * 1.7) * R * 0.09, nx = -Math.sin(ang), ny = Math.cos(ang);
      const c1x = R + Math.cos(ang) * (r0 + (r1 - r0) * 0.35) + nx * w, c1y = R + Math.sin(ang) * (r0 + (r1 - r0) * 0.35) + ny * w;
      const c2x = R + Math.cos(ang) * (r0 + (r1 - r0) * 0.7) - nx * w * 0.6, c2y = R + Math.sin(ang) * (r0 + (r1 - r0) * 0.7) - ny * w * 0.6;
      const path = () => { ctx.beginPath(); ctx.moveTo(x0, y0); ctx.bezierCurveTo(c1x, c1y, c2x, c2y, x1, y1); };
      const a = 0.35 + 0.65 * active;
      ctx.lineCap = "round";
      path(); ctx.strokeStyle = `rgba(${col},${0.10 * a})`; ctx.lineWidth = R * 0.075; ctx.stroke();
      path(); ctx.strokeStyle = `rgba(${col},${0.35 * a})`; ctx.lineWidth = R * 0.036; ctx.stroke();
      path(); ctx.strokeStyle = `rgba(${col},${0.9 * a})`; ctx.lineWidth = R * 0.016; ctx.stroke();
      path(); ctx.strokeStyle = `rgba(${WHITE},${0.55 * a})`; ctx.lineWidth = R * 0.006; ctx.stroke();
      if (active > 0.3 && !reduced) {                             // travelling energy packets (inward; outward when the core dispatches)
        for (let k = 0; k < 2; k++) {
          const raw = (t * (0.3 + 0.3 * cur.speed) + k / 2 + i * 0.17) % 1, p = active === 2 ? raw : 1 - raw, q = 1 - p;
          const bx = q * q * q * x0 + 3 * q * q * p * c1x + 3 * q * p * p * c2x + p * p * p * x1, by = q * q * q * y0 + 3 * q * q * p * c1y + 3 * q * p * p * c2y + p * p * p * y1;
          const g = ctx.createRadialGradient(bx, by, 0, bx, by, R * 0.05); g.addColorStop(0, `rgba(${WHITE},0.95)`); g.addColorStop(0.35, `rgba(${col},0.7)`); g.addColorStop(1, `rgba(${col},0)`);
          ctx.fillStyle = g; ctx.fillRect(bx - R * 0.05, by - R * 0.05, R * 0.1, R * 0.1);
        }
      }
      ctx.fillStyle = `rgba(${col},0.95)`; ctx.beginPath(); ctx.arc(x1, y1, R * 0.014, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(${WHITE},0.9)`; ctx.beginPath(); ctx.arc(x1, y1, R * 0.006, 0, Math.PI * 2); ctx.fill();
    };

    const draw = () => {
      const m = MODE[live.current.mode] || MODE.idle;
      cur.glow += (m.glow - cur.glow) * 0.04;
      cur.speed += (m.speed - cur.speed) * 0.04;
      t += 0.016 * motion; rot += 0.006 * motion * (0.4 + cur.speed);
      const breath = 1 + Math.sin(t * 2.2) * Math.max(0.03, m.breath) + (hive ? hive.kick * 0.045 : 0);
      ctx.clearRect(-off, -off, full, full);
      if (hive) hive.back(ctx, t, 0.016 * motion, cur.speed, cur.glow);
      // ambient violet/cyan field
      const g = ctx.createRadialGradient(R, R, R * 0.2, R, R, R);
      g.addColorStop(0, `rgba(${VIOLET},${0.22 * cur.glow})`); g.addColorStop(0.5, `rgba(${m.hue},${0.07 * cur.glow})`); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
      // tendrils to agents
      const ag = live.current.agents;
      ag.forEach((a, i) => {
        const ang = (i / Math.max(1, ag.length)) * Math.PI * 2 - Math.PI / 2;
        const active = a.state === "receive" ? 2 : a.state === "analyzing" || a.state === "consensus" ? 1 : a.state === "signal" ? 0.7 : 0.2;
        tendril(ang, 0.97, toneCol(a, i), active, i);
      });
      const imgReady = CORE_IMG && CORE_IMG.complete && CORE_IMG.naturalWidth > 0;
      if (imgReady) {
        // === the exact rendered Core (ring + R) with live pulsation ===
        const D = R * 1.16 * breath, ro = D / 2;
        const pg = ctx.createRadialGradient(R, R, ro * 0.7, R, R, ro * 1.6);
        const pulse = 0.55 + 0.45 * Math.sin(t * 2.2);
        pg.addColorStop(0, `rgba(${m.hue},${(0.25 + 0.3 * pulse) * cur.glow})`); pg.addColorStop(0.5, `rgba(${VIOLET},${0.12 * pulse * cur.glow})`); pg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = pg; ctx.fillRect(0, 0, size, size);
        if (!reduced && m.speed > 0.3) {                                  // energy pulse expanding off the ring
          const ph = (t * (0.3 + 0.25 * cur.speed)) % 1;
          ctx.strokeStyle = `rgba(${m.hue},${0.55 * (1 - ph) * cur.glow})`; ctx.lineWidth = 2 * (1 - ph) + 0.3;
          ctx.beginPath(); ctx.arc(R, R, ro * (1.0 + ph * 0.6), 0, Math.PI * 2); ctx.stroke();
        }
        ctx.drawImage(CORE_IMG, R - ro, R - ro, D, D);
        // annulus overlays: mode tint + rotating white highlights (clipped to the energy ring)
        ctx.save();
        ctx.beginPath(); ctx.arc(R, R, ro * 0.76, 0, Math.PI * 2); ctx.arc(R, R, ro * 0.52, 0, Math.PI * 2, true); ctx.clip();
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = `rgba(${m.hue},${(0.10 + 0.12 * pulse) * cur.glow})`; ctx.fillRect(0, 0, size, size);
        for (let k = 0; k < 3; k++) {
          const ang = rot * 1.6 + (k / 3) * Math.PI * 2, hx = R + Math.cos(ang) * ro * 0.64, hy = R + Math.sin(ang) * ro * 0.64;
          const hg = ctx.createRadialGradient(hx, hy, 0, hx, hy, ro * 0.22); hg.addColorStop(0, `rgba(${WHITE},${0.55 * cur.glow})`); hg.addColorStop(1, `rgba(${WHITE},0)`);
          ctx.fillStyle = hg; ctx.fillRect(hx - ro * 0.22, hy - ro * 0.22, ro * 0.44, ro * 0.44);
        }
        ctx.restore();
        // R glow pulse (the glyph itself is the rendered one)
        ctx.save(); ctx.beginPath(); ctx.arc(R, R, ro * 0.5, 0, Math.PI * 2); ctx.clip(); ctx.globalCompositeOperation = "lighter";
        const rg = ctx.createRadialGradient(R, R, 0, R, R, ro * 0.5); rg.addColorStop(0, `rgba(${VIOLET},${0.18 * pulse * cur.glow})`); rg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = rg; ctx.fillRect(0, 0, size, size); ctx.restore();
      } else {
      // faint radial spokes
      for (let k = 0; k < 8; k++) {
        const ang = rot * 0.5 + (k / 8) * Math.PI * 2;
        ctx.strokeStyle = `rgba(${CYAN},${0.10 * cur.glow})`; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(R + Math.cos(ang) * R * 0.44, R + Math.sin(ang) * R * 0.44); ctx.lineTo(R + Math.cos(ang) * R * 0.62, R + Math.sin(ang) * R * 0.62); ctx.stroke();
      }
      // dark segmented mechanical ring
      const ro = R * 0.54, ri = R * 0.46;
      ctx.lineCap = "butt";
      for (let k = 0; k < 8; k++) {
        const a0 = rot * 0.35 + (k / 8) * Math.PI * 2 + 0.05, a1 = a0 + Math.PI * 2 / 8 - 0.1;
        const mg = ctx.createRadialGradient(R, R, ri, R, R, ro); mg.addColorStop(0, "#10172a"); mg.addColorStop(0.5, "#1c2740"); mg.addColorStop(1, "#0a0f1c");
        ctx.strokeStyle = mg; ctx.lineWidth = ro - ri; ctx.beginPath(); ctx.arc(R, R, (ro + ri) / 2, a0, a1); ctx.stroke();
        ctx.strokeStyle = `rgba(${CYAN},${0.35 + 0.4 * cur.glow})`; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(R, R, ri + 1, a0, a1); ctx.stroke();
        ctx.strokeStyle = "rgba(120,140,180,0.35)"; ctx.beginPath(); ctx.arc(R, R, ro - 1, a0, a1); ctx.stroke();
      }
      // energy annulus (violet → mode hue → cyan), rotating, glowing
      const ra = R * 0.385, wa = R * 0.10;
      ctx.save();
      if (hasConic) {
        const cg = ctx.createConicGradient(-rot * 1.4, R, R);
        cg.addColorStop(0, `rgba(${VIOLET},0.95)`); cg.addColorStop(0.3, `rgba(${m.hue},0.95)`); cg.addColorStop(0.55, `rgba(${CYAN},0.95)`); cg.addColorStop(0.8, `rgba(${MAGENTA},0.85)`); cg.addColorStop(1, `rgba(${VIOLET},0.95)`);
        ctx.strokeStyle = cg;
      } else ctx.strokeStyle = `rgba(${m.hue},0.9)`;
      ctx.shadowColor = `rgba(${m.hue},${0.9 * cur.glow})`; ctx.shadowBlur = R * 0.12 * breath;
      ctx.lineWidth = wa; ctx.beginPath(); ctx.arc(R, R, ra, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      // bright travelling highlights on the annulus
      for (let k = 0; k < 3; k++) {
        const ang = rot * 1.4 + (k / 3) * Math.PI * 2, hx = R + Math.cos(ang) * ra, hy = R + Math.sin(ang) * ra;
        const hg = ctx.createRadialGradient(hx, hy, 0, hx, hy, wa * 0.9); hg.addColorStop(0, `rgba(${WHITE},${0.9 * cur.glow})`); hg.addColorStop(1, `rgba(${WHITE},0)`);
        ctx.fillStyle = hg; ctx.fillRect(hx - wa, hy - wa, wa * 2, wa * 2);
      }
      // thin inner tick ring
      ctx.setLineDash([R * 0.02, R * 0.03]); ctx.lineDashOffset = rot * R; ctx.strokeStyle = `rgba(${CYAN},${0.5 + 0.4 * cur.glow})`; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(R, R, R * 0.31, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
      // inner disc
      const core = R * 0.30 * breath;
      const dg = ctx.createRadialGradient(R - core * 0.35, R - core * 0.35, core * 0.1, R, R, core);
      dg.addColorStop(0, "#1a2340"); dg.addColorStop(0.7, "#0b1020"); dg.addColorStop(1, "#070b16");
      ctx.fillStyle = dg; ctx.beginPath(); ctx.arc(R, R, core, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = `rgba(${m.hue},${0.5 + 0.5 * cur.glow})`; ctx.lineWidth = 1.5; ctx.stroke();
      // energy pulse expanding from the core (active modes)
      if (!reduced && m.speed > 0.3) {
        const ph = (t * (0.3 + 0.25 * cur.speed)) % 1;
        ctx.strokeStyle = `rgba(${m.hue},${0.5 * (1 - ph) * cur.glow})`; ctx.lineWidth = 1.5 * (1 - ph) + 0.3;
        ctx.beginPath(); ctx.arc(R, R, R * (0.56 + ph * 0.4), 0, Math.PI * 2); ctx.stroke();
      }
      // the R — heavy italic display glyph, white→lavender, glowing
      ctx.save();
      ctx.translate(R, R + core * 0.06); ctx.transform(1, 0, -0.18, 1, 0, 0);
      ctx.font = `900 ${core * 1.25}px Unbounded, "JetBrains Mono", sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      const tg = ctx.createLinearGradient(-core, -core, core, core); tg.addColorStop(0, `rgba(${WHITE},1)`); tg.addColorStop(1, "rgba(196,181,253,1)");
      ctx.shadowColor = `rgba(${VIOLET},${0.9 * cur.glow})`; ctx.shadowBlur = core * 0.6 * breath;
      ctx.fillStyle = tg; ctx.fillText("R", 0, 0);
      ctx.shadowBlur = 0; ctx.fillText("R", 0, 0);
      ctx.strokeStyle = "rgba(11,16,32,0.9)"; ctx.lineWidth = Math.max(1, core * 0.06);       // brand slash through the leg
      ctx.beginPath(); ctx.moveTo(-core * 0.15, core * 0.62); ctx.lineTo(core * 0.62, -core * 0.05); ctx.stroke();
      ctx.restore();
      }
      if (hive) hive.front(ctx, t, cur.glow);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
}

export const RippCore = ({ mode = "idle", agents = [], size = 320, compact = false }) => {
  const ref = useRef(null);
  const withHive = !compact && size >= 160, off = withHive ? Math.round(size * 0.45) : 0, full = size + off * 2;
  const live = useRef({ mode, agents });
  live.current = { mode, agents };
  useEffect(() => runCore(ref.current, { size, compact, live }), [size, compact]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!withHive) return <canvas ref={ref} style={{ width: size, height: size }} data-testid="ripp-core" data-mode={mode} />;
  return (
    <div style={{ position: "relative", width: size, height: size }} data-testid="ripp-core-wrap">
      <canvas ref={ref} style={{ position: "absolute", left: -off, top: -off, width: full, height: full, pointerEvents: "none" }} data-testid="ripp-core" data-mode={mode} />
    </div>
  );
};
